/**
 * RichTextEditor.tsx
 * V55.32: 면담 내용 리치 텍스트 에디터
 * V55.33: 글자 크기 조절 기능 추가
 *
 * 외부 라이브러리 없이 contentEditable 기반으로 구현
 * 지원 서식: 굵게, 기울임, 밑줄, 순서없는/순서있는 목록, 글자 크기
 */

import React, { useRef, useCallback, useEffect, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  ChevronDown,
} from 'lucide-react';

// 폰트 크기 옵션
const FONT_SIZES = [
  { label: '12', value: '12px' },
  { label: '14', value: '14px' },
  { label: '16', value: '16px' },
  { label: '18', value: '18px' },
  { label: '20', value: '20px' },
  { label: '24', value: '24px' },
];

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder = '내용을 입력하세요...',
  minHeight = '300px',
}: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);
  const lastSetValue = useRef<string>(''); // V55.41: 마지막으로 에디터에 설정한 값 추적
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);

  // V55.41: 초기값 설정 (비동기 데이터 로드 지원 강화)
  // - 최초 1회 초기화
  // - 비동기로 value가 변경되고 에디터가 아직 해당 값을 받지 않았으면 업데이트
  useEffect(() => {
    if (!editorRef.current || !value) return;

    // 초기화되지 않았으면 초기화
    if (!isInitialized.current) {
      editorRef.current.innerHTML = value;
      lastSetValue.current = value;
      isInitialized.current = true;
      return;
    }

    // V55.41: 비동기 로드된 새 value가 있고, 에디터에 아직 반영되지 않았으면 업데이트
    // (사용자가 입력 중이 아닌 경우에만 - 에디터 내용이 lastSetValue와 같거나 비어있을 때)
    const currentContent = editorRef.current.innerHTML;
    const isUserEditing = currentContent !== lastSetValue.current && currentContent.trim() !== '';

    if (!isUserEditing && value !== lastSetValue.current) {
      editorRef.current.innerHTML = value;
      lastSetValue.current = value;
    }
  }, [value]);

  // 에디터 내용 변경 핸들러
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // 서식 적용 함수
  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // V55.33: 폰트 크기 적용 (Selection API 사용)
  const applyFontSize = useCallback((fontSize: string) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setShowFontSizeDropdown(false);
      return;
    }

    const range = selection.getRangeAt(0);

    // 선택된 텍스트가 없으면 무시
    if (range.collapsed) {
      setShowFontSizeDropdown(false);
      editorRef.current?.focus();
      return;
    }

    // 선택된 내용을 span으로 감싸서 font-size 적용
    const span = document.createElement('span');
    span.style.fontSize = fontSize;

    try {
      // 선택된 내용을 추출
      const contents = range.extractContents();
      span.appendChild(contents);
      range.insertNode(span);

      // 선택 영역 유지
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
    } catch (e) {
      console.error('[RichTextEditor] Font size apply error:', e);
    }

    setShowFontSizeDropdown(false);
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showFontSizeDropdown) {
        setShowFontSizeDropdown(false);
      }
    };

    if (showFontSizeDropdown) {
      // 약간의 딜레이 후 리스너 추가 (드롭다운 열기 클릭과 충돌 방지)
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [showFontSizeDropdown]);

  // 툴바 버튼
  const ToolbarButton = ({
    icon: Icon,
    command,
    title,
  }: {
    icon: typeof Bold;
    command: string;
    title: string;
  }) => (
    <button
      type="button"
      onClick={() => execCommand(command)}
      title={title}
      style={{
        padding: '6px 8px',
        background: 'transparent',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#f1f5f9';
        e.currentTarget.style.color = '#1e293b';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.color = '#64748b';
      }}
    >
      <Icon size={18} />
    </button>
  );

  return (
    <div
      style={{
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      {/* 툴바 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          padding: '8px 12px',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
        }}
      >
        {/* V55.33: 폰트 크기 드롭다운 */}
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowFontSizeDropdown(!showFontSizeDropdown);
            }}
            title="글자 크기"
            style={{
              padding: '4px 8px',
              background: showFontSizeDropdown ? '#e2e8f0' : 'transparent',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            <span>크기</span>
            <ChevronDown size={14} />
          </button>

          {showFontSizeDropdown && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '80px',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {FONT_SIZES.map((size) => (
                <button
                  key={size.value}
                  type="button"
                  onClick={() => applyFontSize(size.value)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: size.value,
                    color: '#334155',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {size.label}px
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 8px' }} />

        <ToolbarButton icon={Bold} command="bold" title="굵게 (Ctrl+B)" />
        <ToolbarButton icon={Italic} command="italic" title="기울임 (Ctrl+I)" />
        <ToolbarButton icon={Underline} command="underline" title="밑줄 (Ctrl+U)" />
        <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 8px' }} />
        <ToolbarButton icon={List} command="insertUnorderedList" title="글머리 기호" />
        <ToolbarButton icon={ListOrdered} command="insertOrderedList" title="번호 매기기" />
      </div>

      {/* 에디터 영역 */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        style={{
          minHeight,
          padding: '16px',
          fontSize: '14px',
          lineHeight: '1.8',
          outline: 'none',
          fontFamily: 'inherit',
          overflowY: 'auto',
        }}
        suppressContentEditableWarning
      />

      {/* placeholder 스타일 */}
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          display: block;
        }
        [contenteditable] ul,
        [contenteditable] ol {
          padding-left: 24px;
          margin: 8px 0;
        }
        [contenteditable] li {
          margin: 4px 0;
        }
      `}</style>
    </div>
  );
}
